import React, { useState, useEffect } from "react";

/* ---------------------------------------------------------------
   BootScreen — cinematic loader that animates the HEXA hex logo:
     · spinning + scale-in entrance
     · pulsing glow ring behind
     · typewriter tagline
     · progress bar with staged status lines
--------------------------------------------------------------- */
export default function BootScreen({ onDone }) {
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState(0);
  const [taglineChars, setTaglineChars] = useState(0);

  const TAGLINE = "AI · WEB · OPERATING · SYSTEM";

  const lines = [
    "BOOTING HEXA KERNEL",
    "INITIALIZING NEURAL CORE",
    "MOUNTING SERVICE GRID",
    "AI LINK · READY",
  ];

  // Progress ticker
  useEffect(() => {
    let p = 0;
    const id = setInterval(() => {
      p += Math.random() * 14 + 5;
      if (p >= 100) {
        p = 100;
        clearInterval(id);
        setTimeout(onDone, 620);
      }
      setProgress(p);
      setStage(Math.floor(p / 25));
    }, 210);
    return () => clearInterval(id);
  }, [onDone]);

  // Typewriter tagline — starts after 550ms (so it lands on top of the scale-in)
  useEffect(() => {
    const delay = setTimeout(() => {
      let i = 0;
      const id = setInterval(() => {
        i += 1;
        setTaglineChars(i);
        if (i >= TAGLINE.length) clearInterval(id);
      }, 55);
      return () => clearInterval(id);
    }, 550);
    return () => clearTimeout(delay);
  }, [TAGLINE.length]);

  return (
    <div
      data-testid="boot-screen"
      className="fixed inset-0 z-[2000] flex flex-col items-center justify-center hex-grid-bg"
      style={{
        backgroundImage:
          "linear-gradient(rgba(4,6,16,0.6), rgba(4,6,16,0.6) 25%, rgba(4,6,16,0.6) 75%, rgba(4,6,16,0.6)), url(" +
          (process.env.PUBLIC_URL || "") +
          "/desktop-wallpaper.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat",
        minHeight: "100dvh",
      }}
    >
      {/* Logo + glow rings stack */}
      <div className="boot-logo-wrap">
        {/* Outer pulsing rings */}
        <span className="boot-ring boot-ring-1" />
        <span className="boot-ring boot-ring-2" />
        <span className="boot-ring boot-ring-3" />
        {/* Glow disc behind the logo */}
        <span className="boot-glow" />
        {/* The icon itself — spins in then settles, purple/blue drop-shadow for depth */}
        <img
          src="/hexa-icon.png"
          alt="HEXA"
          className="boot-logo"
          data-testid="boot-logo"
          style={{ filter: "none" }}
        />
      </div>

      {/* Tagline typewriter */}
      <div
        data-testid="boot-tagline"
        className="mono text-[10px] sm:text-[11px] tracking-[0.55em] uppercase text-[#c7c1ff]/85 mt-8 text-center"
        style={{ minHeight: 16 }}
      >
        <span>{TAGLINE.slice(0, taglineChars)}</span>
        <span className="boot-caret" />
      </div>

      {/* Progress bar */}
      <div className="w-72 sm:w-80 mt-10">
        <div className="flex justify-between mono text-[9px] uppercase tracking-widest text-[#c7c1ff]/70 mb-2">
          <span>{lines[Math.min(stage, lines.length - 1)]}</span>
          <span className="text-[#3b82f6]">{Math.floor(progress)}%</span>
        </div>
        <div className="h-px w-full bg-[#8b5cf6]/15 relative overflow-hidden">
          <div
            className="absolute left-0 top-0 h-full"
            style={{
              width: `${progress}%`,
              background: "linear-gradient(90deg, #8b5cf6, #3b82f6)",
              boxShadow: "0 0 12px #8b5cf6",
              transition: "width 210ms linear",
            }}
          />
        </div>
        <div className="mt-3 mono text-[8px] uppercase tracking-[0.3em] text-[#c7c1ff]/40 flex justify-between">
          <span>node://hexa-01</span>
          <span>secure-channel</span>
        </div>
      </div>

      {/* Scoped boot animations */}
      <style>{`
        .boot-logo-wrap {
          position: relative;
          width: clamp(280px, 52vh, 520px);
          height: clamp(280px, 52vh, 520px);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 8px;
        }
        .boot-logo {
          height: 52%;
          width: auto;
          animation:
            boot-spin-in 1.8s cubic-bezier(0.19, 1, 0.22, 1) both,
            boot-drift 6s ease-in-out 1.8s infinite;
          filter: none;
          transform-origin: center center;
          position: relative;
          z-index: 3;
        }
        .boot-glow {
          position: absolute;
          inset: 12%;
          border-radius: 50%;
          background: radial-gradient(circle,
            rgba(139,92,246,0.55) 0%,
            rgba(59,130,246,0.30) 40%,
            transparent 68%);
          filter: blur(28px);
          z-index: 1;
          animation: boot-pulse 3.2s ease-in-out infinite;
        }
        .boot-ring {
          position: absolute;
          border-radius: 50%;
          border: 1px solid rgba(139,92,246,0.35);
          z-index: 2;
          pointer-events: none;
          animation: boot-ripple 4s ease-out infinite;
        }
        .boot-ring-1 {
          width: 60%; height: 60%;
          border-color: rgba(139,92,246,0.55);
          animation-delay: 0.6s;
        }
        .boot-ring-2 {
          width: 80%; height: 80%;
          border-color: rgba(59,130,246,0.35);
          animation-delay: 1.4s;
        }
        .boot-ring-3 {
          width: 100%; height: 100%;
          border-color: rgba(139,92,246,0.20);
          animation-delay: 2.2s;
        }
        .boot-caret {
          display: inline-block;
          width: 6px;
          height: 1em;
          margin-left: 4px;
          vertical-align: -0.18em;
          background: #3b82f6;
          box-shadow: 0 0 8px #3b82f6;
          animation: boot-blink 0.9s steps(1) infinite;
        }

        @keyframes boot-spin-in {
          0%   { opacity: 0; transform: rotate(-540deg) scale(0.05); }
          40%  { opacity: 1; }
          80%  { transform: rotate(-15deg) scale(1.06); }
          100% { opacity: 1; transform: rotate(0deg) scale(1); }
        }
        @keyframes boot-drift {
          0%, 100% { transform: rotate(0deg)  translateY(0px) scale(1); }
          50%      { transform: rotate(6deg)  translateY(-6px) scale(1.02); }
        }
        @keyframes boot-pulse {
          0%, 100% { opacity: 0.75; transform: scale(1); }
          50%      { opacity: 1;    transform: scale(1.08); }
        }
        @keyframes boot-ripple {
          0%   { opacity: 0;    transform: scale(0.55); }
          20%  { opacity: 0.85; }
          100% { opacity: 0;    transform: scale(1.35); }
        }
        @keyframes boot-blink {
          0%, 60% { opacity: 1; }
          61%, 100% { opacity: 0; }
        }

        @media (prefers-reduced-motion: reduce) {
          .boot-logo, .boot-glow, .boot-ring {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
          }
        }
      `}</style>
    </div>
  );
}
